import { useCallback } from 'react';

import { generateUrl } from '@zenbusiness/common-components/SharedAsset';
import Text from '@zenbusiness/common-components/Text';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';

import BubblePicker from '#client/pages/EducationStudio/components/BubblePicker';
import WaltzActivitySection
  from '#client/pages/EducationStudio/components/Interactive/WaltzActivitySection';
import LessonTitle from '#client/pages/EducationStudio/components/LessonTitle';
import { StandardImage } from '#client/pages/EducationStudio/components/multimedia/image/StandardImage';
import { useLessonStorage } from '#client/pages/EducationStudio/hooks';
import { useBreakpoints } from '#client/pages/EducationStudio/hooks/useBreakpoints';
import superstyled from '#client/pages/EducationStudio/utils/superstyled';

const LessonTitleContainer = superstyled(
  {
    tag: LessonTitle,
    slot: 'LessonTitle',
    styles: {
      marginBottom: '1rem'
    }
  }
);

export const ActivityBox = superstyled({
  tag: Box,
  name: 'ActivityBox',
  styles: ({ theme }) => ({
    maxWidth: '100%',
    [theme.breakpoints.up('sm')]: {
      maxWidth: '75%'
    }
  })
});
const ActivityContainer = superstyled({
  tag: Box,
  name: 'ActivityContainer',
  styles: ({ theme }) => ({
    maxWidth: '64rem',
    width: '100vw',
    display: 'grid',
    gridTemplateColumns: '1fr',
    gridColumnGap: '5%',
    justifyContent: 'center',
    margin: 'auto 1.5rem',
    gridRowGap: '1.5rem',
    position: 'relative',
    [theme.breakpoints.up('sm')]: {
      gridRowGap: '1rem',
      gridTemplateColumns: '1.5fr 2fr'
    }
  })
});
export const LeftContainer = superstyled({
  tag: Box,
  styles:  {
    alignSelf: 'center',
    gridRow: 'span 5'
  }
});
const ImageWithTimeOfDayBackground = superstyled({
  tag: Box,
  styles: ({ timeOfDays, time }) => ({
    backgroundImage: `url(${generateUrl(`${timeOfDays[time].image}`, 375, 300, 'auto')})`,
    backgroundSize: '100%, 100%',
    backgroundPositionX: '0',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    height: '18.75rem',
    margin: '0 -1.5rem 1rem -1.5rem'
  })
});

const ActivityDescription = superstyled({
  tag: Box,
  styles: ({ theme }) => ({
    [theme.breakpoints.down('sm')]: {
      backgroundImage: 'linear-gradient(0deg, #000 0%, #000 25%, transparent 100%)',
      margin: '0 -1.5rem',
      padding: '1rem 1.5rem',
      position: 'absolute',
      top: '11.5rem',
      '& p, & h1': {
        color: `${theme.palette.focus00.main} !important`
      }
    }
  })
});

export const NextContainer = superstyled({
  tag: Box,
  styles: ({ theme }) => ({
    gridColumn: 'span 1',
    marginBottom: '1rem',
    [theme.breakpoints.up('sm')]: {
      gridColumn: 'span 2'
    }
  })
});

export const BubblePickerWrapper = superstyled({
  tag: Box,
  styles: ({ theme }) => ({
    gridColumn: 'span 1',
    [theme.breakpoints.up('sm')]: {
      gridColumn: 'span 2'
    }
  })
});

export const ButtonPrimary = superstyled({
  tag: Button,
  name: 'ButtonPrimary',
  slot: 'ButtonPrimary',
  props: { variant: 'contained', color: 'primary', size: 'large' },
  styles: ({ theme }) => ({
    border: `1px solid ${theme.palette.focus00.main}`,
    borderRadius: '1rem',
    backgroundColor: theme.palette.focus90.main,
    color: theme.palette.focus00.main,
    padding: '1.25rem 1.5rem',
    [theme.breakpoints.up('md')]: {
      width: 'unset',
      display: 'flex'
    },
    '&.Mui-disabled': {
      color: theme.palette.focus10.main,
      backgroundColor: theme.palette.focus30.main
    }
  })
});

const WaltzActivityPage = ({
  time, onPrimaryClick
}) => {
  const { lesson } = useParams();
  const {
    sizes: { isMobile }
  } = useBreakpoints();
  const { getStorageValue, setStorageValue, saveChanges } = useLessonStorage(lesson);

  const timeOfDays = {
    morning: {
      title: 'Morning \u2615',
      activities: [
        { text: 'Coffee' },
        { text: 'Networking' },
        { text: 'Emails' },
        { text: 'Brainstorming' },
        { text: 'Finances' },
        { text: 'Social media' },
        { text: 'Exercise' },
        { text: 'Break' },
        { text: 'Collaboration' },
        { text: 'Meetings' },
        { text: 'Creative tasks' },
        { text: 'Customer calls' }
      ],
      maxSelection: 2,
      image: '/illustration/J2S-waltz-exercise-morning.svg',
      imageAlt: 'coffee table',
      sectionTitle: '\u2615 Morning',
      bubbleColor: '#F9C8EE',
      sectionColor: '#F9C8EE'
    },
    afternoon: {
      title: 'Afternoon \u2600\uFE0F',
      activities: [
        { text: 'Coffee' },
        { text: 'Networking' },
        { text: 'Emails' },
        { text: 'Brainstorming' },
        { text: 'Finances' },
        { text: 'Social media' },
        { text: 'Exercise' },
        { text: 'Break' },
        { text: 'Collaboration' },
        { text: 'Meetings' },
        { text: 'Creative tasks' },
        { text: 'Customer calls' }
      ],
      image: '/illustration/J2S-waltz-exercise-afternoon.svg',
      imageAlt: 'coffee table',
      maxSelection: 2,
      sectionTitle: '\u2600\uFE0F Afternoon',
      bubbleColor: '#EB72D9',
      sectionColor: '#EB72D9'
    },
    evening: {
      title: 'Evening \u{1F315}',
      activities: [
        { text: 'Coffee' },
        { text: 'Networking' },
        { text: 'Emails' },
        { text: 'Brainstorming' },
        { text: 'Finances' },
        { text: 'Social media' },
        { text: 'Exercise' },
        { text: 'Break' },
        { text: 'Collaboration' },
        { text: 'Meetings' },
        { text: 'Creative tasks' },
        { text: 'Customer calls' }
      ],
      image: '/illustration/J2S-waltz-exercise-evening.svg',
      imageAlt: 'coffee table',
      maxSelection: 2,
      sectionTitle: '\u{1F315} Evening',
      bubbleColor: '#BB38C2',
      sectionColor: '#BB38C2'
    }
  };

  const isEmpty = (value) => value === '';

  const getStorageProp = (prop) => {
    const value = getStorageValue(prop);
    if (isEmpty(value)) return [];
    return value;
  };

  const onNextHandler = useCallback(() => {
    saveChanges();
    if (onPrimaryClick) onPrimaryClick();
  }, [saveChanges, onPrimaryClick]);

  const handleBubbleSelection = (option) => {
    const selectedBubbles = getStorageProp(time);
    if (selectedBubbles.some((selected) => selected.text === option.text)) {
      setStorageValue(time, selectedBubbles.filter((value) => value.text !== option.text));
    } else if (selectedBubbles.length < timeOfDays[time].maxSelection) {
      setStorageValue(time, [...selectedBubbles, option]);
    }
  };
  return (
    <Box width="100%" justifyContent="center" display="flex">
      <ActivityContainer>
        {!isMobile ? (
          <LeftContainer>
            <StandardImage
              src={timeOfDays[time].image}
              width={400}
              alt={timeOfDays[time].imageAlt}
            />
          </LeftContainer>
        ) : (
          <ImageWithTimeOfDayBackground />
        )}
        <ActivityDescription>
          <Text size="sm" bold>EXERCISE</Text>
          <LessonTitleContainer title={timeOfDays[time].title} />
          <Text size="lg">
            To start, select at least two activities that you enjoy doing in the
            {' '}
            {time}
            .
          </Text>
        </ActivityDescription>
        <ActivityBox>
          <WaltzActivitySection
            sectionTitle={timeOfDays[time].sectionTitle}
            options={getStorageProp(time)}
            bubbleColor={timeOfDays[time].bubbleColor}
            sectionColor={timeOfDays[time].sectionColor}
          />
        </ActivityBox>
        <BubblePickerWrapper>
          <BubblePicker
            options={timeOfDays[time].activities}
            selectedBubbles={getStorageProp(time)}
            onClick={handleBubbleSelection}
            completed={getStorageProp(time).length >= timeOfDays[time].maxSelection}
          />
        </BubblePickerWrapper>
        <NextContainer>
          <ButtonPrimary
            onClick={() => onNextHandler()}
            disabled={getStorageProp(time).length < timeOfDays[time].maxSelection}
            variant="contained"
            color="primary"
            size="large"
          >
            Next
          </ButtonPrimary>
        </NextContainer>
      </ActivityContainer>
    </Box>
  );
};

WaltzActivityPage.propTypes = {
  time: PropTypes.oneOf(['morning', 'afternoon', 'evening']),
  onPrimaryClick: PropTypes.func
};

WaltzActivityPage.defaultProps = {
  time: 'morning'
};

export default WaltzActivityPage;
